import { reactive, UnwrapRef, Ref, ref, computed, ComputedRef } from '@vue/composition-api';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import { i18n_t } from '@/locales/i18n';
import tips from '@/renderer/scripts/utils/tips';
import smh, { SMH } from '@/renderer/scripts/smh';
import store from '@/renderer/store';
import ipc from '@/renderer/scripts/utils/ipc';
import { isAdminOrSuperAdmin } from '@/renderer/scripts/utils/UserRole/getters';
import { IS_PRIVATE } from '@/common/constant/platform';
import { CONSTANT_URL } from '@/renderer/scripts/http';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';

type ClassifyList = {
  hasreadList: SMH.MessageItem[];
  unreadList: SMH.MessageItem[];
};
type Handler = {
  columns: ComputedRef<Array<any>>;
  messageData: SMH.ListMessageData;
  classifyList: UnwrapRef<ClassifyList>;
  pageData: UnwrapRef<CoFile.PageData>;
  loading: Ref<boolean>;
  selectMode: Ref<boolean>;
  selectedRowKeys: Ref<Array<String | Number>>;
  methods: {
    pullList: (type: number) => Promise<void>;
    readAllMessage: (type: number) => Promise<void>;
    arrangeList: (list: SMH.MessageItem[]) => void;
    deleteAll: (type: number) => Promise<void>;
    deleteItemList: (type: number, messageIdList: number[]) => Promise<void>;
    getIconName: (iconType: String) => string;
    getIconColorStyle: (iconType: String) => string;
    clickLink: (data: Record<string, any>) => void;
  };
};
export default function handler(): Handler {
  const loading = ref(false);
  const selectMode = ref(false);
  const selectedRowKeys: Ref<Array<String | Number>> = ref([]);
  const messageData = reactive<SMH.ListMessageData>({
    totalNum: 0,
    unreadMessageNumber: 0,
    unreadSystemMessageNumber: 0,
    unreadWarnMessageNumber: 0,
    contents: [],
    allMessageNum: 0,
  });
  const classifyList = reactive<ClassifyList>({
    hasreadList: [],
    unreadList: [],
  });
  const pageData = reactive({
    currPage: 1,
    total: 0,
    pageSize: 20,
  });
  const selectColumns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      width: 40,
      className: 'notic-center__table-checkbox',
    },
    {
      colKey: 'detail',
      width: 588,
      className: 'notic-center__table-detail',
    },
  ];
  const generalColumns = [
    {
      colKey: 'detail',
      width: 628,
      className: 'notic-center__table-detail',
    },
  ];

  // 初始化columns
  const columns = computed(() => (selectMode.value ? selectColumns : generalColumns));
  const methods = {
    // 处理收到新消息
    handleMessage(data: any) {
      if (data && data.type === 'message') {
        const unreadMessageNumber = store.getters.messageNumber + 1 < 100 ? store.getters.messageNumber + 1 : '...';
        store.commit('SET_MESSAGE_NUMBER', unreadMessageNumber);
      }
    },
    async pullList(type: number) {
      loading.value = true;
      try {
        const data = await smh.userAPI.listMessage({
          type,
          page: pageData.currPage,
          pageSize: pageData.pageSize,
        });
        const result = data.data;
        selectedRowKeys.value = [];
        messageData.contents = [];
        messageData.contents.push(...result.contents);
        messageData.unreadMessageNumber = result.unreadMessageNumber;
        messageData.unreadWarnMessageNumber = result.unreadWarnMessageNumber;
        messageData.unreadSystemMessageNumber = result.unreadSystemMessageNumber;
        messageData.allMessageNum = result.allMessageNum;
        pageData.total = result.totalNum;
        loading.value = false;
      } catch (e) {
        tips.error(e as MsgError);
        loading.value = false;
      }
    },
    async readAllMessage(type: number) {
      try {
        await smh.userAPI.readAllMessage({
          type,
        });
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    async deleteAll(type: number) {
      try {
        await smh.userAPI.deleteAllMessage({
          type,
        });
        tips.success(i18n_t('删除成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    async deleteItemList(type: number, messageIdList: number[]) {
      try {
        await smh.userAPI.deleteMessageList({
          type,
          messageIdList,
        });
        tips.success(i18n_t('删除成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    // 自定义列表区分已读未读和全部，目前暂时不用
    arrangeList(list: SMH.MessageItem[]) {
      classifyList.hasreadList = [];
      classifyList.unreadList = [];
      if (list?.length) {
        for (const ele of list) {
          if (ele.hasRead) {
            classifyList.hasreadList.push(ele);
          } else {
            classifyList.unreadList.push(ele);
          }
        }
      }
    },
    handleSelectRows(value: Array<String | Number>, { selectedRowData }: any) {
      selectedRowKeys.value = value;
      // tableData.selectedList = selectedRowData;
    },

    getIconName(iconType: String) {
      let iconName = '';
      switch (iconType) {
        case 'add-circle': {
          iconName = 'add-circle';
          break;
        }
        case 'warn-circle': {
          iconName = 'error-circle';
          break;
        }
        case 'minus-circle': {
          iconName = 'minus-circle';
          break;
        }
        default:
          iconName = 'error-circle';
          break;
      }
      return iconName;
    },
    getIconColorStyle(iconType: String) {
      let colorStyle = '';
      switch (iconType) {
        case 'add-circle':
        case 'minus-circle': {
          colorStyle = 'color: black';
          break;
        }
        case 'warn-circle': {
          colorStyle = 'color: orange';
          break;
        }
        default:
          colorStyle = 'color: orange';
          break;
      }
      return colorStyle;
    },
    toUpgradeTips() {
      // 私有化版本不进行处理
      if (IS_PRIVATE) return;
      const dialog = CustomDialog.confirm({
        header: i18n_t('温馨提示'),
        body: i18n_t('当前版本过低，请升级至最新版本查看'),
        confirmBtn: i18n_t('立即升级'),
        cancelBtn: i18n_t('取消'),
        onConfirm: () => {
          dispatchRouter(CONSTANT_URL.officialWebsiteDowloadUrl);
        },
        onClose: () => {
          dialog.hide?.();
          dialog.destroy?.();
        },
      });
    },
    async checkPathExist(spaceId: string, path: string) {
      try {
        await smh.commonAPI.inspectFile({
          spaceId,
          path,
        });
        return true;
      } catch (e: any) {
        if (e.status === 403) {
          // 没有权限
          throw { code: 'AccessDenied' };
        }
      }
      // 再检查是否存在同名文件夹
      try {
        await smh.commonAPI.inspectDirectory({
          spaceId,
          dirPath: path,
        });
        return true;
      } catch (e: any) {
        if (e.status === 403) {
          // 没有权限
          throw { code: 'AccessDenied' };
        }
      }

      throw { code: 'PathNotFound' };
    },
    async jumpRelatedToMe(data: Record<string, any>) {
      ipc.emit(ipc.RENDERER_EVENTS_MAP.NOTICE_CENTER_SHOW, { show: false });
      if (data.spaceId && data.filePath) {
        try {
          await methods.checkPathExist(data.spaceId, data.filePath.join('/'));
        } catch (e: any) {
          if (e.code === 'PathNotFound') {
            tips.error(i18n_t('文件/文件夹不存在'));
            return;
          }
          if (e.code === 'AccessDenied') {
            tips.error(i18n_t('当前无访问权限'));
            return;
          }
        }
      }
      dispatchRouter({ name: 'relatedToMe' });
    },
    clickLink(data: Record<string, any>) {
      switch (data.linkType) {
        case 'outside': {
          if (data.linkUrl !== undefined) {
            dispatchRouter(data.linkUrl);
          }
          break;
        }
        case 'inside-user-management': {
          if (isAdminOrSuperAdmin.value) {
            // 隐藏消息中心
            ipc.emit(ipc.RENDERER_EVENTS_MAP.NOTICE_CENTER_SHOW, { show: false });
            dispatchRouter({ name: 'userManagement' });
          }
          break;
        }
        case 'show-virus-file':
          // 跳转病毒专区
          ipc.emit(ipc.RENDERER_EVENTS_MAP.NOTICE_CENTER_SHOW, { show: false });
          dispatchRouter({ name: 'suspiciousFile' });
          break;
        case 'file-collect':
          // 跳到文件收集
          ipc.emit(ipc.RENDERER_EVENTS_MAP.NOTICE_CENTER_SHOW, { show: false });
          dispatchRouter({ name: 'fileCollect', query: { tab: 'hasCollected' } });
          break;
        case 'sensitive-word':
          // 跳到文件收集
          ipc.emit(ipc.RENDERER_EVENTS_MAP.NOTICE_CENTER_SHOW, { show: false });
          dispatchRouter({ name: 'securityScan' });
          break;
        // 跳转到待审批
        case 'inside-apply-authority-list-my-audit':
          ipc.emit(ipc.RENDERER_EVENTS_MAP.NOTICE_CENTER_SHOW, { show: false });
          dispatchRouter({ name: 'fileApply', query: { tab: 'applying' } });
          break;
        // 跳转到我的审批
        case 'inside-apply-authority-list-my-apply':
          ipc.emit(ipc.RENDERER_EVENTS_MAP.NOTICE_CENTER_SHOW, { show: false });
          dispatchRouter({ name: 'fileApply', query: { tab: 'myApply' } });
          break;
        case 'inside-related-to-me':
          // 跳到与我相关
          methods.jumpRelatedToMe(data);
          break;
        case 'inside-send-log-list':
          // 跳到日志列表
          dispatchRouter({ name: 'sendLog' });
          break;
        default:
          this.toUpgradeTips();
          break;
      }
      reporter.report('notification', {
        notification_click: data.linkType,
      });
    },
  };
  return {
    messageData,
    methods,
    classifyList,
    pageData,
    loading,
    columns,
    selectMode,
    selectedRowKeys,
  };
}
